import React from 'react';
import { Box } from '@mui/material';
import Header from './Header';
import Footer from './Footer';
import Navigation from './Navigation';
import SearchDialog from '../Search/SearchDialog';
import { useAuth } from '../../auth/AuthContext';
import { AuthModalContext } from '../../auth/AuthModalContext';
import AuthModal from '../auth/AuthModal';
import LearnerOnboardingModal from '../cohort/LearnerOnboardingModal';
import AccountProfileModal from '../account/AccountProfileModal';
import { useLocation } from 'react-router-dom';
import { recordAuditEvent } from '../../api/auditApi';
import { recordAnalyticsEvent } from '../../api/analyticsApi';

const Layout = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = React.useState(false);
  const [authModalMode, setAuthModalMode] = React.useState('register');
  const [isOnboardingOpen, setIsOnboardingOpen] = React.useState(false);
  const [isAccountOpen, setIsAccountOpen] = React.useState(false);
  const { user } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    recordAnalyticsEvent('page_view', { path: location.pathname, signed_in: Boolean(user) }, location.pathname);
    if (!user) return;
    recordAuditEvent('page_view', { path: location.pathname }).catch(() => {});
  }, [location.pathname, user]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authMode = params.get('auth');
    if (!user && ['register', 'login'].includes(authMode)) {
      setAuthModalMode(authMode);
      setIsAuthModalOpen(true);
    }
  }, [location.search, user]);

  const openSearch = () => {
    recordAnalyticsEvent('search_opened', { signed_in: Boolean(user) }, location.pathname);
    if (user) {
      recordAuditEvent('search_opened').catch(() => {});
      setIsSearchOpen(true);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const openOnboarding = () => {
    recordAnalyticsEvent('onboarding_explainer_opened', { signed_in: Boolean(user) }, location.pathname);
    if (user) {
      setIsOnboardingOpen(true);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  return (
    <AuthModalContext.Provider value={{ openAuthModal: (mode = 'register') => { setAuthModalMode(mode); setIsAuthModalOpen(true); } }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header
        onOpenMenu={() => setIsDrawerOpen(true)}
        onOpenSearch={openSearch}
        onOpenAuth={() => { setAuthModalMode('login'); setIsAuthModalOpen(true); }}
        onOpenOnboarding={openOnboarding}
        onOpenAccount={() => setIsAccountOpen(true)}
      />

      <Box component="main" sx={{ flexGrow: 1, pt: { xs: 7, sm: 8 }, pb: 3 }}>
        {children}
      </Box>

      <Footer />

      <Navigation
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpenSearch={() => {
          setIsDrawerOpen(false);
          openSearch();
        }}
        onOpenOnboarding={openOnboarding}
        onOpenAccount={() => setIsAccountOpen(true)}
      />

      <SearchDialog open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <LearnerOnboardingModal
        open={isOnboardingOpen && Boolean(user)}
        onClose={() => setIsOnboardingOpen(false)}
        user={user}
      />

      <AccountProfileModal open={isAccountOpen && Boolean(user)} onClose={() => setIsAccountOpen(false)} />

      <AuthModal open={isAuthModalOpen && !user} onClose={() => setIsAuthModalOpen(false)} initialMode={authModalMode} />
    </Box>
    </AuthModalContext.Provider>
  );
};

export default Layout;
